import type { ListResponse } from 'Services/SalesManagement/types'
import type { LoadingStates } from 'Store'
import {
  createAxiosAsyncThunk,
  createAxiosAsyncThunkWithParams,
  type DynamicUrl,
  getAxiosInstance,
  type ValidationErrors
} from 'Util/ApiClient'
import actionGenerator from 'Util/actionGenerator'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios, { type AxiosError } from 'axios'

export enum NotificationType {
  webShop = '1',
  affiliate = '2',
  authority = '3'
}

export type NotificationTypeOptions =
  | NotificationType.webShop
  | NotificationType.affiliate
  | NotificationType.authority

export interface WebShopNotificationShape {
  id: number
  created: Date
  active_from: Date | null
  active_to: Date | null
  authority_name: string
  type: string
  content_en: string | null
  content_fi: string | null
  content_sv: string | null
  author_email: string | null
}

export interface WebShopNotificationDetailShape {
  id: number | null
  active_from: Date | null
  active_to: Date | null
  authority_code: string
  type: string
  content_en: string
  content_fi: string
  content_sv: string
  edit_events: NotificationEditEvent[]
}

export interface Authority {
  id: number
  authority_label: string
}

export interface Operator {
  id: number
  name: string
}

export interface NotificationState {
  webShopNotifications: WebShopNotificationShape[]
  affiliateNotifications: AffiliateNotificationShape[]
  authorityNotifications: AuthorityNotificationShape[]
  authorityNotificationsByAuthority: AuthorityNotificationShape[]
  count: number
  next: string | null
  previous: string | null
  loading: LoadingStates
  submitLoading: LoadingStates
  deleteLoading: LoadingStates
  singleWebShopNotification: WebShopNotificationDetailShape | null
  singleAffiliateNotification: AffiliateNotificationDetailShape | null
  singleAuthorityNotification: AuthorityNotificationDetailShape | null
  webShopNotificationOptions: WebShopNotificationOptions
  affiliateNotificationOptions: AffiliateNotificationOptions
  authorityNotificationOptions: AuthorityNotificationOptions
  affiliateNotificationOperators: Operator[]
  affiliateNotificationOperatorsLoading: LoadingStates
  webShopNotificationAuthorities: Authority[]
  webShopNotificationAuthoritiesLoading: LoadingStates
  authorityNotificationAuthorities: Authority[]
  authorityNotificationAuthoritiesLoading: LoadingStates
}

const initialState: NotificationState = {
  webShopNotifications: [],
  webShopNotificationAuthorities: [],
  webShopNotificationAuthoritiesLoading: 'idle',
  affiliateNotifications: [],
  affiliateNotificationOperators: [],
  authorityNotifications: [],
  authorityNotificationsByAuthority: [],
  authorityNotificationAuthorities: [],
  authorityNotificationAuthoritiesLoading: 'idle',
  count: 0,
  next: null,
  previous: null,
  loading: 'idle',
  submitLoading: 'idle',
  deleteLoading: 'idle',
  affiliateNotificationOperatorsLoading: 'idle',
  singleWebShopNotification: null,
  singleAffiliateNotification: null,
  singleAuthorityNotification: null,
  webShopNotificationOptions: {
    authorities: [],
    type: []
  },
  affiliateNotificationOptions: {
    operators: [],
    type: []
  },
  authorityNotificationOptions: {
    authorities: [],
    type: []
  }
}

type FetchWebShopNotificationResponse = WebShopNotificationDetailShape

export interface NotificationParamShape {
  page: number
  ordering: string
  editor: string
  operator: number | ''
  authority: number | 'general' | ''
}

const fetchWebShopNotification = createAxiosAsyncThunkWithParams<
  FetchWebShopNotificationResponse,
  DynamicUrl
>('notification/fetchWebShopNotification', '/api/v1/notifications/webstore')

type FetchWebShopNotificationListParams = NotificationParamShape

export interface WebShopNotificationOptions {
  authorities: []
  type: []
}

const fetchWebShopNotificationList = createAxiosAsyncThunkWithParams<
  ListResponse<WebShopNotificationShape>,
  FetchWebShopNotificationListParams | undefined
>('notification/fetchWebShopNotificationList', '/api/v1/notifications/webstore/')

interface FetchWebShopNotificationOptions {
  field_choices: WebShopNotificationOptions
}

const fetchWebShopNotificationOptions = createAxiosAsyncThunk<
  FetchWebShopNotificationOptions,
  undefined
>('notification/fetchWebShopNotificationOptions', '/api/v1/notifications/webstore/', 'OPTIONS')

type CreateWebShopNotificationRequest = WebShopNotificationDetailShape

type CreateWebShopNotificationResponse = WebShopNotificationDetailShape

const createWebShopNotification = createAsyncThunk<
  CreateWebShopNotificationResponse,
  CreateWebShopNotificationRequest,
  {
    rejectValue: ValidationErrors
  }
>('notification/createWebShopNotification', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().post<CreateWebShopNotificationResponse>(
      `/api/v1/notifications/webstore/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type ChangeWebShopNotificationRequest = WebShopNotificationDetailShape

type ChangeWebShopNotificationResponse = WebShopNotificationDetailShape

const changeWebShopNotification = createAsyncThunk<
  ChangeWebShopNotificationResponse,
  ChangeWebShopNotificationRequest,
  {
    rejectValue: ValidationErrors
  }
>('notification/changeWebShopNotification', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().put<ChangeWebShopNotificationResponse>(
      `/api/v1/notifications/webstore/${fields.id}/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type DeleteWebShopNotificationResponse = Record<string, never>

const deleteWebShopNotification = createAxiosAsyncThunkWithParams<
  DeleteWebShopNotificationResponse,
  DynamicUrl
>('notification/deleteWebShopNotification', '/api/v1/notifications/webstore', 'DELETE')

/** Affiliate **/

type OptionType = [number, string]

export interface NotificationEditEvent {
  id: number
  created: Date
  editor_email: string
}

export interface AffiliateNotificationShape {
  id: number
  created: Date
  active_from: Date | null
  active_to: Date | null
  type: string
  title_en: string
  title_fi: string
  title_sv: string
  content_en: string
  content_fi: string
  content_sv: string
  operators: OptionType[]
  author_email: string | null
}

export interface AffiliateNotificationDetailShape {
  id: number | null
  active_from: Date | null
  active_to: Date | null
  type: string
  title_en: string
  title_fi: string
  title_sv: string
  content_en: string
  content_fi: string
  content_sv: string
  operators: string[]
  edit_events: NotificationEditEvent[]
}

type FetchAffiliateNotificationResponse = AffiliateNotificationDetailShape

const fetchAffiliateNotification = createAxiosAsyncThunkWithParams<
  FetchAffiliateNotificationResponse,
  DynamicUrl
>('notification/fetchAffiliateNotification', '/api/v1/notifications/affiliate')

type FetchAffiliateNotificationListParams = NotificationParamShape

export interface AffiliateNotificationOptions {
  operators: []
  type: []
}

const fetchAffiliateNotificationList = createAxiosAsyncThunkWithParams<
  ListResponse<AffiliateNotificationShape>,
  FetchAffiliateNotificationListParams | undefined
>('notification/fetchAffiliateNotificationList', '/api/v1/notifications/affiliate/')

interface FetchAffiliateNotificationOptions {
  field_choices: AffiliateNotificationOptions
}

const fetchAffiliateNotificationOptions = createAxiosAsyncThunk<
  FetchAffiliateNotificationOptions,
  undefined
>('notification/fetchAffiliateNotificationOptions', '/api/v1/notifications/affiliate/', 'OPTIONS')

type CreateAffiliateNotificationRequest = AffiliateNotificationDetailShape

type CreateAffiliateNotificationResponse = AffiliateNotificationDetailShape

const createAffiliateNotification = createAsyncThunk<
  CreateAffiliateNotificationResponse,
  CreateAffiliateNotificationRequest,
  {
    rejectValue: ValidationErrors
  }
>('notification/createAffiliateNotification', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().post<CreateAffiliateNotificationResponse>(
      `/api/v1/notifications/affiliate/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type ChangeAffiliateNotificationRequest = AffiliateNotificationDetailShape

type ChangeAffiliateNotificationResponse = AffiliateNotificationDetailShape

const changeAffiliateNotification = createAsyncThunk<
  ChangeAffiliateNotificationResponse,
  ChangeAffiliateNotificationRequest,
  {
    rejectValue: ValidationErrors
  }
>('notification/changeAffiliateNotification', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().put<ChangeAffiliateNotificationResponse>(
      `/api/v1/notifications/affiliate/${fields.id}/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type DeleteAffiliateNotificationResponse = Record<string, never>

const deleteAffiliateNotification = createAxiosAsyncThunkWithParams<
  DeleteAffiliateNotificationResponse,
  DynamicUrl
>('notification/deleteAffiliateNotification', '/api/v1/notifications/affiliate', 'DELETE')

/** Authority **/

export interface AuthorityNotificationShape {
  id: number
  created: Date
  active_from: Date | null
  active_to: Date | null
  type: string
  content_en: string
  content_fi: string
  content_sv: string
  authority_code: string
  authority_name: string
  author_email: string | null
}

export interface AuthorityNotificationDetailShape {
  id: number | null
  active_from: Date | null
  active_to: Date | null
  type: string
  content_en: string
  content_fi: string
  content_sv: string
  authority_code: string
  edit_events: NotificationEditEvent[]
}

type FetchAuthorityNotificationResponse = AuthorityNotificationDetailShape

const fetchAuthorityNotification = createAxiosAsyncThunkWithParams<
  FetchAuthorityNotificationResponse,
  DynamicUrl
>('notification/fetchAuthorityNotification', '/api/v1/notifications/authority')

type FetchAuthorityNotificationListParams = NotificationParamShape

export interface AuthorityNotificationOptions {
  authorities: []
  type: []
}

const fetchAuthorityNotificationList = createAxiosAsyncThunkWithParams<
  ListResponse<AuthorityNotificationShape>,
  FetchAuthorityNotificationListParams | undefined
>('notification/authorityNotificationList', '/api/v1/notifications/authority/')

interface FetchAuthorityNotificationListByAuthorityResponse {
  count: number
  next: string | null
  previous: string | null
  results: AuthorityNotificationShape[]
}

const fetchAuthorityNotificationListByAuthority = createAxiosAsyncThunk<
  FetchAuthorityNotificationListByAuthorityResponse,
  undefined
>(
  'notification/authorityNotificationListByAuthority',
  '/api/v1/notifications/authority/by-authority/'
)

interface FetchAuthorityNotificationOptions {
  field_choices: AuthorityNotificationOptions
}

const fetchAuthorityNotificationOptions = createAxiosAsyncThunk<
  FetchAuthorityNotificationOptions,
  undefined
>('notification/fetchAuthorityNotificationOptions', '/api/v1/notifications/authority/', 'OPTIONS')

type CreateAuthorityNotificationRequest = AuthorityNotificationDetailShape

type CreateAuthorityNotificationResponse = AuthorityNotificationDetailShape

const createAuthorityNotification = createAsyncThunk<
  CreateAuthorityNotificationResponse,
  CreateAuthorityNotificationRequest,
  {
    rejectValue: ValidationErrors
  }
>('notification/createAuthorityNotification', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().post<CreateAuthorityNotificationResponse>(
      `/api/v1/notifications/authority/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type ChangeAuthorityNotificationRequest = AuthorityNotificationDetailShape

type ChangeAuthorityNotificationResponse = AuthorityNotificationDetailShape

const changeAuthorityNotification = createAsyncThunk<
  ChangeAuthorityNotificationResponse,
  ChangeAuthorityNotificationRequest,
  {
    rejectValue: ValidationErrors
  }
>('notification/changeAuthorityNotification', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().put<ChangeAuthorityNotificationResponse>(
      `/api/v1/notifications/authority/${fields.id}/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type DeleteAuthorityNotificationResponse = Record<string, never>

const deleteAuthorityNotification = createAxiosAsyncThunkWithParams<
  DeleteAuthorityNotificationResponse,
  DynamicUrl
>('notification/deleteAuthorityNotification', '/api/v1/notifications/authority', 'DELETE')

const genericActions = actionGenerator('notification')

type FetchAffiliateNotificationOperatorsResponse = Operator[]

const fetchAffiliateNotificationOperators = createAxiosAsyncThunk<
  FetchAffiliateNotificationOperatorsResponse,
  undefined
>('notification/affiliate/fetchOperators', '/api/v1/notifications/affiliate/operators/')

type FetchWebShopNotificationAuthoritiesResponse = Authority[]
const fetchWebShopNotificationAuthorities = createAxiosAsyncThunk<
  FetchWebShopNotificationAuthoritiesResponse,
  undefined
>('notification/webshop/fetchAuthorities', '/api/v1/notifications/webstore/authorities/')

type FetchAuthorityNotificationAuthoritiesResponse = Authority[]
const fetchAuthorityNotificationAuthorities = createAxiosAsyncThunk<
  FetchAuthorityNotificationAuthoritiesResponse,
  undefined
>('notification/authority/fetchAuthorities', '/api/v1/notifications/authority/authorities/')

const notificationSlice = createSlice({
  name: genericActions.name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchWebShopNotification.fulfilled, (state, { payload }) => {
        state.singleWebShopNotification = payload
        state.loading = 'fulfilled'
      })
      .addCase(fetchWebShopNotification.pending, state => {
        state.singleWebShopNotification = initialState.singleWebShopNotification
        state.loading = 'pending'
      })
      .addCase(fetchWebShopNotification.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(fetchWebShopNotificationList.fulfilled, (state, { payload }) => {
        state.webShopNotifications = payload.results
        state.count = payload.count
        state.next = payload.next
        state.previous = payload.previous
        state.loading = 'fulfilled'
      })
      .addCase(fetchWebShopNotificationList.pending, state => {
        state.webShopNotifications = initialState.webShopNotifications
        state.loading = 'pending'
      })
      .addCase(fetchWebShopNotificationList.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(fetchWebShopNotificationOptions.fulfilled, (state, { payload }) => {
        state.webShopNotificationOptions = payload.field_choices
        state.loading = 'fulfilled'
      })
      .addCase(fetchWebShopNotificationOptions.pending, state => {
        state.webShopNotificationOptions = initialState.webShopNotificationOptions
        state.loading = 'pending'
      })
      .addCase(fetchWebShopNotificationOptions.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(createWebShopNotification.fulfilled, (state, { payload }) => {
        state.singleWebShopNotification = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(createWebShopNotification.pending, state => {
        state.singleWebShopNotification = initialState.singleWebShopNotification
        state.submitLoading = 'pending'
      })
      .addCase(createWebShopNotification.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(changeWebShopNotification.fulfilled, (state, { payload }) => {
        state.singleWebShopNotification = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(changeWebShopNotification.pending, state => {
        state.singleWebShopNotification = initialState.singleWebShopNotification
        state.submitLoading = 'pending'
      })
      .addCase(changeWebShopNotification.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(deleteWebShopNotification.fulfilled, state => {
        state.deleteLoading = 'fulfilled'
      })
      .addCase(deleteWebShopNotification.pending, state => {
        state.deleteLoading = 'pending'
      })
      .addCase(deleteWebShopNotification.rejected, state => {
        state.deleteLoading = 'rejected'
      })

      /** Affiliate **/
      .addCase(fetchAffiliateNotification.fulfilled, (state, { payload }) => {
        state.singleAffiliateNotification = payload
        state.loading = 'fulfilled'
      })
      .addCase(fetchAffiliateNotification.pending, state => {
        state.singleAffiliateNotification = initialState.singleAffiliateNotification
        state.loading = 'pending'
      })
      .addCase(fetchAffiliateNotification.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(fetchAffiliateNotificationList.fulfilled, (state, { payload }) => {
        state.affiliateNotifications = payload.results
        state.count = payload.count
        state.next = payload.next
        state.previous = payload.previous
        state.loading = 'fulfilled'
      })
      .addCase(fetchAffiliateNotificationList.pending, state => {
        state.affiliateNotifications = initialState.affiliateNotifications
        state.count = initialState.count
        state.next = initialState.next
        state.previous = initialState.previous
        state.loading = 'pending'
      })
      .addCase(fetchAffiliateNotificationList.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(fetchAffiliateNotificationOptions.fulfilled, (state, { payload }) => {
        state.affiliateNotificationOptions = payload.field_choices
        state.loading = 'fulfilled'
      })
      .addCase(fetchAffiliateNotificationOptions.pending, state => {
        state.affiliateNotificationOptions = initialState.affiliateNotificationOptions
        state.loading = 'pending'
      })
      .addCase(fetchAffiliateNotificationOptions.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(createAffiliateNotification.fulfilled, (state, { payload }) => {
        state.singleAffiliateNotification = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(createAffiliateNotification.pending, state => {
        state.singleAffiliateNotification = initialState.singleAffiliateNotification
        state.submitLoading = 'pending'
      })
      .addCase(createAffiliateNotification.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(changeAffiliateNotification.fulfilled, (state, { payload }) => {
        state.singleAffiliateNotification = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(changeAffiliateNotification.pending, state => {
        state.singleAffiliateNotification = initialState.singleAffiliateNotification
        state.submitLoading = 'pending'
      })
      .addCase(changeAffiliateNotification.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(deleteAffiliateNotification.fulfilled, state => {
        state.deleteLoading = 'fulfilled'
      })
      .addCase(deleteAffiliateNotification.pending, state => {
        state.deleteLoading = 'pending'
      })
      .addCase(deleteAffiliateNotification.rejected, state => {
        state.deleteLoading = 'rejected'
      })

      /** Authority **/
      .addCase(fetchAuthorityNotification.fulfilled, (state, { payload }) => {
        state.singleAuthorityNotification = payload
        state.loading = 'fulfilled'
      })
      .addCase(fetchAuthorityNotification.pending, state => {
        state.singleAuthorityNotification = initialState.singleAuthorityNotification
        state.loading = 'pending'
      })
      .addCase(fetchAuthorityNotification.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(fetchAuthorityNotificationList.fulfilled, (state, { payload }) => {
        state.authorityNotifications = payload.results
        state.count = payload.count
        state.next = payload.next
        state.previous = payload.previous
        state.loading = 'fulfilled'
      })
      .addCase(fetchAuthorityNotificationList.pending, state => {
        state.authorityNotifications = initialState.authorityNotifications
        state.loading = 'pending'
      })
      .addCase(fetchAuthorityNotificationList.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(fetchAuthorityNotificationListByAuthority.fulfilled, (state, { payload }) => {
        state.authorityNotificationsByAuthority = payload.results
        state.loading = 'fulfilled'
      })
      .addCase(fetchAuthorityNotificationListByAuthority.pending, state => {
        state.authorityNotificationsByAuthority = initialState.authorityNotificationsByAuthority
        state.loading = 'pending'
      })
      .addCase(fetchAuthorityNotificationListByAuthority.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(fetchAuthorityNotificationOptions.fulfilled, (state, { payload }) => {
        state.authorityNotificationOptions = payload.field_choices
        state.loading = 'fulfilled'
      })
      .addCase(fetchAuthorityNotificationOptions.pending, state => {
        state.authorityNotificationOptions = initialState.authorityNotificationOptions
        state.loading = 'pending'
      })
      .addCase(fetchAuthorityNotificationOptions.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(createAuthorityNotification.fulfilled, (state, { payload }) => {
        state.singleAuthorityNotification = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(createAuthorityNotification.pending, state => {
        state.singleAuthorityNotification = initialState.singleAuthorityNotification
        state.submitLoading = 'pending'
      })
      .addCase(createAuthorityNotification.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(changeAuthorityNotification.fulfilled, (state, { payload }) => {
        state.singleAuthorityNotification = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(changeAuthorityNotification.pending, state => {
        state.singleAuthorityNotification = initialState.singleAuthorityNotification
        state.submitLoading = 'pending'
      })
      .addCase(changeAuthorityNotification.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(deleteAuthorityNotification.fulfilled, state => {
        state.deleteLoading = 'fulfilled'
      })
      .addCase(deleteAuthorityNotification.pending, state => {
        state.deleteLoading = 'pending'
      })
      .addCase(deleteAuthorityNotification.rejected, state => {
        state.deleteLoading = 'rejected'
      })
      .addCase(fetchAffiliateNotificationOperators.fulfilled, (state, { payload }) => {
        state.affiliateNotificationOperatorsLoading = 'fulfilled'
        state.affiliateNotificationOperators = payload
      })
      .addCase(fetchAffiliateNotificationOperators.pending, state => {
        state.affiliateNotificationOperatorsLoading = 'pending'
      })
      .addCase(fetchAffiliateNotificationOperators.rejected, state => {
        state.affiliateNotificationOperatorsLoading = 'rejected'
      })
      .addCase(genericActions.ResetAction, () => initialState)

      .addCase(fetchWebShopNotificationAuthorities.fulfilled, (state, { payload }) => {
        state.webShopNotificationAuthoritiesLoading = 'fulfilled'
        state.webShopNotificationAuthorities = payload
      })
      .addCase(fetchWebShopNotificationAuthorities.pending, state => {
        state.webShopNotificationAuthoritiesLoading = 'pending'
      })
      .addCase(fetchWebShopNotificationAuthorities.rejected, state => {
        state.webShopNotificationAuthoritiesLoading = 'rejected'
      })
      .addCase(fetchAuthorityNotificationAuthorities.fulfilled, (state, { payload }) => {
        state.authorityNotificationAuthoritiesLoading = 'fulfilled'
        state.authorityNotificationAuthorities = payload
      })
      .addCase(fetchAuthorityNotificationAuthorities.pending, state => {
        state.authorityNotificationAuthoritiesLoading = 'pending'
      })
      .addCase(fetchAuthorityNotificationAuthorities.rejected, state => {
        state.authorityNotificationAuthoritiesLoading = 'rejected'
      })
    // .addMatcher(genericActions.isPendingAction, state => {
    //   state.loading = 'pending'
    // })
    // .addMatcher(genericActions.isRejectedAction, state => {
    //   state.loading = 'rejected'
    // })
    // .addMatcher(genericActions.isFulfilledAction, state => {
    //   state.loading = 'fulfilled'
    // })
  }
})

export default notificationSlice.reducer

export {
  fetchWebShopNotificationOptions,
  fetchWebShopNotification,
  fetchWebShopNotificationList,
  fetchWebShopNotificationAuthorities,
  deleteWebShopNotification,
  changeWebShopNotification,
  createWebShopNotification,
  fetchAffiliateNotificationOptions,
  fetchAffiliateNotification,
  fetchAffiliateNotificationOperators,
  fetchAffiliateNotificationList,
  deleteAffiliateNotification,
  changeAffiliateNotification,
  createAffiliateNotification,
  fetchAuthorityNotificationOptions,
  fetchAuthorityNotification,
  fetchAuthorityNotificationList,
  fetchAuthorityNotificationListByAuthority,
  fetchAuthorityNotificationAuthorities,
  deleteAuthorityNotification,
  changeAuthorityNotification,
  createAuthorityNotification
}
